import { Component, ErrorHandler, OnInit, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { FieldTree, form, pattern, required } from '@angular/forms/signals'
import { MatDialogRef } from '@angular/material/dialog'

import { KirjanpitajanRooli } from '@lemon/core-firebase/lemonator/model/kirjanpitaja'
import { DateService } from '@lemon/core/service/date.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { validoiReaktiivisesti } from 'app/_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { KirjanpitajanToimipiste, LisaaUusiKirjanpitajaPyynto, LisaaUusiKirjanpitajaVastaus } from 'app/_jaettu-lemonator/model/kirjanpitaja'
import { KirjanpitajanToimipisteService } from 'app/_jaettu-lemonator/service/kirjanpitajan-toimipiste.service'
import { Observable } from 'rxjs'
import { takeWhile } from 'rxjs/operators'

interface KirjanpitajaLisaaUusiLomake {
  etunimi: string
  sukunimi: string
  email: string
  puhelin: string
  aloitti: Date | null
  toimipiste: string
  tutkintoKlt: boolean
  msUid: string
  rooli: KirjanpitajanRooli | null
}

@Component({
  templateUrl: './kirjanpitaja-lisaa-uusi.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class KirjanpitajaLisaaUusiDialog implements OnInit {
  lomake: FieldTree<KirjanpitajaLisaaUusiLomake>
  kaikkiToimipisteet: KirjanpitajanToimipiste[] = []
  readonly commonError = signal<string | null>(null)

  naytaRooliKenttaObservable: Observable<boolean>

  // KirjanpitajanRooli-enumi templaattia varten
  // eslint-disable-next-line @typescript-eslint/naming-convention
  readonly KirjanpitajanRooli = KirjanpitajanRooli

  private readonly _varatutEmailit = signal<Set<string>>(new Set())
  private readonly _injector = inject(Injector)

  constructor(
    private _dialogRef: MatDialogRef<KirjanpitajaLisaaUusiDialog, string | undefined>,
    private _firebase: FirebaseLemonator,
    private _ladataanService: LadataanService,
    private _validationService: FormValidationService,
    private _dateService: DateService,
    private _errorHandler: ErrorHandler,
    private _kirjanpitajanToimipisteService: KirjanpitajanToimipisteService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _kirjanpitajaService: KirjanpitajaService,
  ) {}

  ngOnInit() {
    this.kaikkiToimipisteet = this._kirjanpitajanToimipisteService.annaKaikkiToimipisteet()
    // Roolikenttä näytetään vain IT/KAYTTOTUKI-tiimin jäsenille. Backend tarkistaa saman uudelleen.
    this.naytaRooliKenttaObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaTaiTukiObservable
    this.lomake = form(
      signal<KirjanpitajaLisaaUusiLomake>({ etunimi: '', sukunimi: '', email: '', puhelin: '', aloitti: null, toimipiste: '', tutkintoKlt: false, msUid: '', rooli: null }),
      polku => {
        required(polku.etunimi)
        required(polku.sukunimi)
        required(polku.email)
        validoiReaktiivisesti(polku.email, this._validateEmail)
        validoiReaktiivisesti(polku.email, this._validateEmailEiKaytossa)
        pattern(polku.puhelin, /^[\d ()+-]+$/)
        required(polku.aloitti)
        required(polku.toimipiste)
        required(polku.msUid)
      },
      { injector: this._injector },
    )

    // Backend tarkistaa saman uudelleen, mutta tehdään tarkistus jo lomakkeella käyttäjäkokemuksen takia.
    this._kirjanpitajaService.kirjanpitajienNimitiedotObservable.pipe(takeWhile(nimitiedot => !nimitiedot?.length, true)).subscribe(nimitiedot => {
      this._varatutEmailit.set(new Set((nimitiedot ?? []).filter(n => !n.poistettu && n.email).map(n => n.email.trim().toLowerCase())))
    })
  }

  private _requestRunning = false
  async tallenna() {
    if (this._requestRunning) {
      return
    }
    try {
      this._requestRunning = true

      if (!this.lomake().valid()) {
        this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
        return
      }

      const values = this.lomake().value()
      const pyynto: LisaaUusiKirjanpitajaPyynto = {
        etunimi: values.etunimi.trim(),
        sukunimi: values.sukunimi.trim(),
        email: values.email.trim(),
        puhelin: values.puhelin ? values.puhelin.trim() : null,
        aloitti: this._dateService.dateToNumber(values.aloitti),
        toimipisteAvain: values.toimipiste,
        tutkintoKlt: !!values.tutkintoKlt,
        msUid: values.msUid.trim(),
        rooli: values.rooli ?? null,
      }

      this._ladataanService.aloitaLataaminen()
      this.commonError.set(null)

      const vastaus = await this._firebase.functionsCall<LisaaUusiKirjanpitajaPyynto, LisaaUusiKirjanpitajaVastaus>('kirjanpitajaLisaaUusi', pyynto)
      if (!vastaus) {
        this.commonError.set(this._kaannaVirhe(undefined))
        return
      }
      if (vastaus.e && !vastaus.kirjanpitajaAvain) {
        this.commonError.set(this._kaannaVirhe(vastaus.e))
        return
      }
      if (vastaus.e && vastaus.kirjanpitajaAvain) {
        // Kirjanpitäjä luotiin onnistuneesti, mutta jokin lisävaihe (esim. rooli) epäonnistui.
        // Suljetaan dialogi ja siirrytään muokkaussivulle, mutta näytetään virheviesti snackbar-tyyliin
        // jättämällä commonError-tila (ei näy enää koska dialog sulkeutuu); IT/KAYTTOTUKI näkee
        // tilanteen edit-formissa.
        this._dialogRef.close(vastaus.kirjanpitajaAvain)
        return
      }
      this._dialogRef.close(vastaus.kirjanpitajaAvain)
    } catch (err: any) {
      this._errorHandler.handleError(err)
      this.commonError.set('Tallentamisen aikana tapahtui virhe. Ole hyvä ja ilmoita tästä ylläpidolle. Tekninen virhe: "' + err.message + '".')
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._requestRunning = false
    }
  }

  peruuta() {
    this._dialogRef.close()
  }

  naytaVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    const tila = kentta()
    return tila.errors().some(virhe => virhe.kind === laji) && (tila.dirty() || tila.touched())
  }

  private _validateEmail: ValidatorFn = (ctrl: AbstractControl): ValidationErrors | null => {
    if (ctrl.value == null || ctrl.value === undefined || ctrl.value.trim() === '') {
      return null
    }
    return Validators.email(ctrl)
  }

  private _validateEmailEiKaytossa: ValidatorFn = (ctrl: AbstractControl): ValidationErrors | null => {
    const value: string = ctrl.value
    if (!value || typeof value !== 'string') {
      return null
    }
    const normalized = value.trim().toLowerCase()
    if (!normalized) {
      return null
    }
    return this._varatutEmailit().has(normalized) ? { 'email-varattu': true } : null
  }

  private _kaannaVirhe(virhe: LisaaUusiKirjanpitajaVastaus['e']): string {
    if (virhe === 'email-already-exists') {
      return 'Sähköpostiosoite on jo käytössä.'
    }
    if (virhe === 'unauthorized') {
      return 'Sinulla ei ole oikeuksia luoda uusia kirjanpitäjiä.'
    }
    if (virhe === 'missing-data') {
      return 'Pakollisia tietoja puuttuu.'
    }
    return 'Tallentamisen aikana tapahtui virhe. Ole hyvä ja ilmoita tästä ylläpidolle.'
  }
}
